import type { ToolCall } from '@/schemas/commons';

export type ToolStatusTone = 'success' | 'warning' | 'error' | 'unknown';

export type ToolStatus = {
	/** HTTP-style code, or null when nothing was recorded for this call. */
	code: number | null;
	tone: ToolStatusTone;
	/** False when the backend reported the real code, true when we inferred it. */
	derived: boolean;
};

const ERROR_PATTERN = /^\s*(error|exception|traceback|timeout|failed)/i;

const toneForCode = (code: number): ToolStatusTone => {
	if (code >= 500) return 'error';
	if (code >= 400) return 'error';
	if (code === 204) return 'warning';
	return 'success';
};

const isEmptyOutput = (output: unknown): boolean => {
	if (Array.isArray(output)) return output.length === 0;
	if (typeof output === 'string') return output.trim().length === 0;
	if (output !== null && typeof output === 'object') return Object.keys(output).length === 0;
	return false;
};

/** An `error` key that actually carries something — `{ error: null }` is not a failure. */
const errorField = (output: unknown): unknown => {
	if (output === null || typeof output !== 'object' || Array.isArray(output)) return null;
	const value = (output as Record<string, unknown>).error;

	return value === null || value === undefined || value === '' ? null : value;
};

const looksLikeError = (output: unknown): boolean => {
	if (typeof output === 'string') return ERROR_PATTERN.test(output);
	return errorField(output) !== null;
};

/** A validation complaint is the caller's fault (4xx); anything else is the tool's (5xx). */
const isArgumentError = (output: unknown): boolean => {
	const value = errorField(output) ?? output;
	const text = typeof value === 'string' ? value : JSON.stringify(value ?? '');

	return /invalid|validation|missing|required|argument|bad request/i.test(text);
};

/** What each recorded failure maps to, when no real service code came with it. */
const CODE_BY_ERROR_TYPE: Record<string, number> = {
	invalid_arguments: 400,
	tool_exception: 500,
	upstream_http: 502,
};

/**
 * Status of a tool call, real when the backend reports it and inferred when it
 * does not.
 *
 * Three sources, in order of how much they are worth: the real status code of
 * the underlying service, the verdict the backend recorded (`status` +
 * `error.type`, back#SPEC-011), and — only when neither exists — a guess read
 * off the output string.
 *
 * The default is deliberately `unknown`, never success: `output` is null
 * whenever no `role='tool'` row correlated, which is the *common* case in
 * production (back#SPEC-006). Painting those green would turn absence of
 * evidence into evidence of success — the exact failure front#SPEC-003 exists
 * to fix.
 */
export const deriveToolStatus = (tool: Pick<ToolCall, 'output' | 'statusCode' | 'status' | 'error'>): ToolStatus => {
	if (tool.statusCode != null) return { code: tool.statusCode, tone: toneForCode(tool.statusCode), derived: false };
	if (tool.status === 'error') {
		const code = CODE_BY_ERROR_TYPE[tool.error?.type ?? ''] ?? 500;

		return { code, tone: 'error', derived: false };
	}
	// A recorded success with no service code still beats reading the string:
	// the system says it worked, so the badge does not say `derivado`.
	if (tool.status === 'success') return { code: 200, tone: 'success', derived: false };
	if (tool.output === null || tool.output === undefined) return { code: null, tone: 'unknown', derived: true };
	if (looksLikeError(tool.output)) {
		const code = isArgumentError(tool.output) ? 400 : 500;

		return { code, tone: 'error', derived: true };
	}
	if (isEmptyOutput(tool.output)) return { code: 204, tone: 'warning', derived: true };
	return { code: 200, tone: 'success', derived: true };
};
